import { Component } from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { HistoryUtil, SchemaUtil, getThumbUrl as imageGetThumbUrl } from '../../utils'
import { CoreService, NotificationService } from '../../services'
import { ProductMovePack } from 'depoto-core/src/entities/ProductMovePack'
import { File, ProductMove } from 'depoto-core/src/entities'
import { TranslateService } from '@ngx-translate/core'
import { ScrollService } from '../../services/scroll.service'

import * as Sentry from '@sentry/angular-ivy'
import { CompanyUtilService } from '../../services/company-util.service'

@Component({
  selector: 'product-move-pack-page',
  templateUrl: 'product-move-pack.component.html',
  styleUrls: ['product-move-pack.component.scss'],
})
export class ProductMovePackComponent {
  productMovePack: ProductMovePack
  canCreateOrEdit = false
  loading = true
  quantityItemsSum: number[] | number = 0
  userCanDeleteProductMoves = false
  public atLeastOneProductHasBatchExpirationModeEnabled = true

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    public readonly scrollService: ScrollService,
    public companyUtil: CompanyUtilService,
  ) {
    this.route.params.forEach((params: Params) => {
      const id = +params['id']
      this.refreshData(id)
    })
  }

  async refreshData(id: number) {
    this.loading = true
    const result = await this.core.services.productMovePack.getById(id, SchemaUtil.productMovePack)
    this.userCanDeleteProductMoves =
      this.core.services.user.hasRole('ROLE_PRODUCT_MOVE_PACK_DELETE') ||
      this.core.services.user.hasRole('ROLE_ADMIN') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
    this.loading = false
    if (result) {
      this.productMovePack = result
      this.atLeastOneProductHasBatchExpirationModeEnabled = !!result.moves.find(
        value => value.product.enabledBatchExpiration === true,
      )
      this.quantityItemsSum = this.productMovePack.moves.map(i => i.amount).reduce((a, b) => Number(a) + Number(b), 0)
    } else {
      Sentry.captureMessage('Access denied to ProductMove!, redirecting back to login...')
    }
  }

  async createMoveInFromMoveOut() {
    const movePack: ProductMovePack = new ProductMovePack({
      type: { id: 'in' },
      note: 'auto: out -> in',
      depotTo: this.productMovePack.depotFrom,
      moves: [
        ...this.productMovePack.moves.map(m => ({
          amount: m.amount,
          product:
            m.orderItem?.product?.id > 0
              ? m.orderItem?.product
              : m.productDepotFrom?.product?.id > 0
                ? m.productDepotFrom.product
                : m.productDepotTo.product,
          purchasePrice: m.purchasePrice,
          purchasePriceSum: Number(m.purchasePrice) * Number(m.amount),
          depotTo: m.productDepotFrom?.depot?.id,
          supplier: m.productDepotFrom?.supplier?.id,
          batch: m.batch,
          expirationDate: m.expirationDate,
          position1: m.position1,
          position2: m.position2,
          position3: m.position3,
        })),
      ],
    })
    window['pmp-clone'] = movePack
    this.router.navigate(['stock-batch'])
  }

  navigateBack(): void {
    HistoryUtil.navigateBack()
  }

  downloadPdf() {
    if (!this.productMovePack) {
      return
    }
    this.core.services.download.downloadDocumentAndShowInNewWindow(this.productMovePack.url)
  }

  getPurchasePriceAmountSum(purchasePrice: number, amount: number): string {
    return (amount * purchasePrice).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  getPurchasePriceInTotal() {
    if (this.productMovePack.type?.id === 'in') {
      const total = Number(
        this.productMovePack.moves.reduce((currentTotal, move) => {
          return Number(move.productDepotTo.purchasePrice) * move.amount + currentTotal
        }, 0),
      )
      return total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    } else {
      const total = this.productMovePack.moves.reduce((currentTotal, move) => {
        return Number(move.productDepotFrom.purchasePrice * move.amount + currentTotal)
      }, 0)
      return total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    }
  }

  public async deleteProductMovePack() {
    if (!confirm(this.translateService.instant('productMovePack.delete.confirmation'))) {
      return new Promise<void>(r => r())
    }
    this.loading = true
    try {
      const alert = this.productMovePack.type.name.includes('Výdejka')
        ? 'alert.product.move.pack.out.deleted'
        : this.productMovePack.type.name.includes('Příjemka')
          ? 'alert.product.move.pack.in.deleted'
          : 'alert.product.move.pack.transfer.deleted'
      this.productMovePack = await this.core.services.productMovePack.delete(this.productMovePack)
      this.notificationService.success(this.translateService.instant(alert))
      this.scrollService.clearValue(this.core.services.storage.keys.views.productMovePackList, 'productMovePacks')
    } catch (e) {
      console.log(e)
    }
    this.loading = false
    await this.router.navigate(['/product-move-packs'])
  }

  public async deleteProductMove(move: ProductMove) {
    if (!confirm(this.translateService.instant('productMovePack.delete.confirmation'))) {
      return new Promise<void>(r => r())
    }
    this.loading = true
    try {
      await this.core.services.productMove.delete(move)
      this.notificationService.success(this.translateService.instant('alert.product.move.deleted'))
      this.refreshData(this.productMovePack.id)
    } catch (e) {
      console.log(e)
    }
    this.loading = false
  }

  getProductMovePackTotalAmount() {
    return this.productMovePack.moves.reduce((currentTotal, item) => {
      return item.amount + currentTotal
    }, 0)
  }

  getThumbUrl(image: File, format: 'w135wp' | 'w2048wp' = 'w135wp'): string {
    return imageGetThumbUrl(image, format)
  }

  triggerBackBtn() {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      this.router.navigate(['/product-move-packs'])
    }
  }
}
